import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useStore } from '../context/StoreContext';

/**
 * SEO 元件 - 動態管理 meta tags, Open Graph, Schema.org
 * 從 StoreContext 取得租戶資料，不再硬編碼
 */
const SEO = ({
  title,
  description,
  keywords,
  image,
  url,
  type = 'website',
  product = null,
  noindex = false
}) => {
  const { storeConfig, seoConfig, storeSettings } = useStore();

  const pickFirst = (...values) => values.find((value) => typeof value === 'string' && value.trim() !== '') || '';

  const siteName = pickFirst(
    seoConfig?.site_name,
    seoConfig?.site_title,
    storeSettings?.site_name,
    storeSettings?.site_title,
    storeConfig?.store_name,
    'URStore'
  );
  const resolvedDescription = pickFirst(
    description,
    seoConfig?.default_description,
    seoConfig?.meta_description,
    storeSettings?.default_description,
    storeSettings?.meta_description,
    storeConfig?.description
  );
  const resolvedKeywords = pickFirst(
    keywords,
    seoConfig?.default_keywords,
    seoConfig?.meta_keywords,
    storeSettings?.default_keywords,
    storeSettings?.meta_keywords
  );
  const googleVerification = pickFirst(
    seoConfig?.google_verification,
    seoConfig?.google_site_verification,
    storeSettings?.google_verification,
    storeSettings?.google_site_verification
  );
  const bingVerification = pickFirst(
    seoConfig?.bing_verification,
    seoConfig?.msvalidate_01,
    seoConfig?.bing_site_verification,
    storeSettings?.bing_verification,
    storeSettings?.msvalidate_01,
    storeSettings?.bing_site_verification
  );
  const siteUrl = window.location.origin;
  const defaultImage = storeConfig?.og_image_url
    ? (storeConfig.og_image_url.startsWith('http') ? storeConfig.og_image_url : `${siteUrl}${storeConfig.og_image_url}`)
    : `${siteUrl}/og-image.jpg`;

  const fullTitle = title ? `${title} | ${siteName}` : siteName;
  const fullUrl = url ? `${siteUrl}${url}` : siteUrl;
  const imageUrl = image
    ? (image.startsWith('http') ? image : `${siteUrl}${image}`)
    : defaultImage;

  // 麵包屑結構化資料（商品頁）
  const getBreadcrumbSchema = () => {
    if (!product) return null;

    return {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: '首頁',
          item: siteUrl
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: '全部商品',
          item: `${siteUrl}/products`
        },
        ...(product.category_name ? [{
          '@type': 'ListItem',
          position: 3,
          name: product.category_name,
          item: `${siteUrl}/products?category=${product.category_id}`
        }] : []),
        {
          '@type': 'ListItem',
          position: product.category_name ? 4 : 3,
          name: product.name
        }
      ]
    };
  };

  return (
    <Helmet>
      {/* 基礎 Meta */}
      <title>{fullTitle}</title>
      <meta name="description" content={resolvedDescription} />
      {resolvedKeywords && <meta name="keywords" content={resolvedKeywords} />}
      {googleVerification && <meta name="google-site-verification" content={googleVerification} />}
      {bingVerification && <meta name="msvalidate.01" content={bingVerification} />}
      <link rel="canonical" href={fullUrl} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type === 'product' ? 'product' : 'website'} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={resolvedDescription} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:locale" content="zh_TW" />

      {/* Facebook Product Tags */}
      {product && (
        <>
          <meta property="product:price:amount" content={product.sale_price} />
          <meta property="product:price:currency" content="TWD" />
          <meta property="product:availability" content={product.quantity > 0 ? 'in stock' : 'out of stock'} />
          <meta property="product:condition" content="new" />
          <meta property="product:retailer_item_id" content={product.sku} />
        </>
      )}

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={resolvedDescription} />
      <meta name="twitter:image" content={imageUrl} />

      {/* LINE 分享 */}
      <meta property="line:title" content={title || siteName} />
      <meta property="line:description" content={resolvedDescription} />

      {/* Schema.org - Breadcrumb（僅商品頁） */}
      {product && (
        <script type="application/ld+json">
          {JSON.stringify(getBreadcrumbSchema())}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
